import React, { useState, ReactNode } from 'react';
import styled from '@emotion/styled';

const Frame = styled.div({
    overflow: 'visible',
    position: 'relative',
});

type ListProps = {
    expanded: boolean;
    direction: 'UP' | 'DOWN';
}

const ListBox = styled.div<ListProps>(props => ({
    visibility: props.expanded ? 'visible' : 'hidden',
    overflowY: 'auto',
    maxHeight: '80vh',
    minWidth: '160px',
    backgroundColor: 'white',
    position: 'absolute',
    top: props.direction == 'DOWN' ? '100%' : 'unset',
    bottom: props.direction == 'UP' ? '100%' : 'unset',
    left: 0,
    padding: '5px 0',
    margin: '2px',
    border: '1px solid rgba(0, 0, 0, .15)',
    borderRadius: '4px',
    boxShadow: '0 6px 12px rgba(0, 0, 0, .175)',
    zIndex: 999,
    ['> *']: {
        color: '#333',
        fontSize: '14px',
        margin: '4px 0px 4px 0px',
        ':hover': {
            backgroundColor: '#ccc',
        },
    },
}));

export type DropdownListProps<T> = React.PropsWithChildren<{
    className?: string;
    values: T[];
    direction?: 'UP' | 'DOWN';
    onSelect: (value: T) => void;
    renderValue: (value: T) => React.ReactNode;
}>;

export function DropdownList<T>(props: DropdownListProps<T>) {
    const { renderValue } = props;

    const [listVisible, setListVisible] = useState(false);

    return (
        <Frame className={props.className} onClick={() => setListVisible(!listVisible)}>
            {props.children}
            <ListBox expanded={listVisible} direction={props.direction || 'DOWN'}>
                {
                    props.values.map((g, i) =>
                        <div onClick={() => props.onSelect(g)} key={i}>{renderValue(g)}</div>
                    )
                }
            </ListBox>
        </Frame>
    );
}

function defaultValueRenderer<T>(value: T) {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return <span>{`${value}`}</span>;
}

export function createDropdownList<T>(valueRenderer?: (value: T) => ReactNode) {
    return (props: Omit<DropdownListProps<T>, 'renderValue'>) => {
        return DropdownList<T>({ ...props, renderValue: valueRenderer ?? defaultValueRenderer });
    };
}
